









import {
  computed,
  defineComponent,
  ref,
  useContext,
  watch,
} from '@nuxtjs/composition-api';
import ShoppingCart, {
  LineItem,
} from '@/modules/SelfServiceRegistration/components/ShoppingCart/ShoppingCart.vue';
import useTrials, { trialPrice } from '@/hooks/useTrials';
import useRegistration, {
  RegistrationStateNames,
} from '@/modules/SelfServiceRegistration/hooks/useRegistration';
import { fetchCorePlanCatalog } from '@/services/PricePlans';
import BillingService, { calculateSalesTax } from '@/services/billing.service';

const toCents = (amount: number) => Math.round(amount * 100);

/**
 * The tax preview only knows Stripe plans, so a Recurly plan is previewed through its Stripe
 * twin: same product, band, billing period and price (as app's useRecurlyCheckout does).
 */
export const findTaxPlanId = (plan: any, stripePlans: any[] = []) =>
  stripePlans.find(
    (p) =>
      p.productKey === plan.productKey &&
      p.tierNumber === plan.tierNumber &&
      p.billingPeriod === plan.billingPeriod &&
      toCents(p.price) === toCents(plan.price)
  )?.value || null;

/**
 * The $1 trial order summary (BIO-2152): what convert charges today for `plan`, with the
 * plan's own setup fee struck through, not a hardcoded amount.
 */
export default defineComponent({
  name: 'CoreSubscriptionShoppingCart',
  components: {
    ShoppingCart,
  },
  props: {
    /** The Recurly plan convert is sent (PaymentPage's checkoutPlan). */
    plan: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    // @ts-ignore
    const { $axios } = useContext();
    BillingService($axios);
    const { trialDuration } = useTrials();
    const { state: registrationState } = useRegistration();

    // Null when there is no Stripe twin to price through: tax is then added at charge.
    const tax = ref<number | null>(null);
    const isReady = ref(false);

    watch(
      () => [props.plan.value, registrationState[RegistrationStateNames.ZIP]],
      async () => {
        isReady.value = false;
        tax.value = null;
        const zip = registrationState[RegistrationStateNames.ZIP];
        const twin = findTaxPlanId(
          props.plan,
          await fetchCorePlanCatalog().catch(() => [])
        );
        if (twin && zip) {
          const breakdown = await calculateSalesTax(
            twin,
            String(zip),
            props.plan.setupFee,
            1
          );
          tax.value = breakdown?.trialTax ?? null;
        }
        isReady.value = true;
      },
      { immediate: true }
    );

    const lineItems = computed<LineItem[]>(() => [
      {
        label: `${trialDuration.value} Day Trial`,
        price: trialPrice,
      },
      {
        label: 'One-Time HR Setup Fee',
        originalPrice: props.plan.setupFee,
        price: 0,
      },
      {
        label: 'Estimated Sales Tax',
        tooltip:
          tax.value === null
            ? 'Sales tax is added at charge, based on your business address.'
            : 'The final tax and total is determined by your business address.',
        price: tax.value,
        type: 'tax',
      },
    ]);

    // With no tax preview the total is $1 and Recurly adds tax at charge, so the label says so
    // rather than quoting a total that is short by the tax (BIO-2158 review).
    const totalItem = computed(() => ({
      label:
        tax.value === null ? 'Total Due Today, before tax' : 'Total Due Today',
      price: trialPrice + (tax.value || 0),
    }));

    return {
      isReady,
      lineItems,
      totalItem,
    };
  },
});
